import {
  clampSliceCount,
  selectionGridSize,
  selectionToCorners,
  type PixelRect,
  type SheetCropSelection,
  type SheetSliceGrid,
} from "@/home/sheet-crop";

const MIN_CANVAS_LONG_EDGE = 280;
const FRAME_MIN_PX = 24;
const HANDLE_R = 14;

export type HomeSheetGridCropOptions = {
  onInteractionChange?: () => void;
  /** Fires when user finishes a new grid marquee (release after drag-select). */
  onGridSelectionCommit?: () => void;
};

export type HomeSheetGridCropHandle = {
  destroy: () => void;
  setImage: (image: HTMLImageElement | null) => void;
  setGrid: (grid: SheetSliceGrid) => void;
  getGrid: () => SheetSliceGrid;
  getFrameRect: () => PixelRect | null;
  getSelection: () => SheetCropSelection | null;
  clearSelection: () => void;
  repaint: () => void;
};

type CropTool = "frame" | "grid";
type DragKind =
  | "frame-move"
  | "frame-resize"
  | { type: "grid-select"; anchor: { col: number; row: number } }
  | { type: "selection-move"; anchor: { col: number; row: number } };

function clampFrame(rect: PixelRect, iw: number, ih: number): PixelRect {
  const sw = Math.max(FRAME_MIN_PX, Math.min(rect.sw, iw));
  const sh = Math.max(FRAME_MIN_PX, Math.min(rect.sh, ih));
  const sx = Math.max(0, Math.min(rect.sx, iw - sw));
  const sy = Math.max(0, Math.min(rect.sy, ih - sh));
  return { sx, sy, sw, sh };
}

function fullImageFrame(iw: number, ih: number): PixelRect {
  return { sx: 0, sy: 0, sw: iw, sh: ih };
}

function resizeFrameUniform(
  corner: "nw" | "ne" | "sw" | "se",
  pt: { x: number; y: number },
  fr: PixelRect,
  aspect: number,
  iw: number,
  ih: number,
): PixelRect {
  const safeAspect = aspect > 0 ? aspect : 1;
  if (corner === "se") {
    const ax = fr.sx;
    const ay = fr.sy;
    let w = Math.max(FRAME_MIN_PX, pt.x - ax);
    let h = Math.max(FRAME_MIN_PX, pt.y - ay);
    if (w / h > safeAspect) h = w / safeAspect;
    else w = h * safeAspect;
    return clampFrame({ sx: ax, sy: ay, sw: w, sh: h }, iw, ih);
  }
  if (corner === "nw") {
    const ax = fr.sx + fr.sw;
    const ay = fr.sy + fr.sh;
    let w = Math.max(FRAME_MIN_PX, ax - pt.x);
    let h = Math.max(FRAME_MIN_PX, ay - pt.y);
    if (w / h > safeAspect) h = w / safeAspect;
    else w = h * safeAspect;
    return clampFrame({ sx: ax - w, sy: ay - h, sw: w, sh: h }, iw, ih);
  }
  if (corner === "ne") {
    const ax = fr.sx;
    const ay = fr.sy + fr.sh;
    let w = Math.max(FRAME_MIN_PX, pt.x - ax);
    let h = Math.max(FRAME_MIN_PX, ay - pt.y);
    if (w / h > safeAspect) h = w / safeAspect;
    else w = h * safeAspect;
    return clampFrame({ sx: ax, sy: ay - h, sw: w, sh: h }, iw, ih);
  }
  const ax = fr.sx + fr.sw;
  const ay = fr.sy;
  let w = Math.max(FRAME_MIN_PX, ax - pt.x);
  let h = Math.max(FRAME_MIN_PX, pt.y - ay);
  if (w / h > safeAspect) h = w / safeAspect;
  else w = h * safeAspect;
  return clampFrame({ sx: ax - w, sy: ay, sw: w, sh: h }, iw, ih);
}

export function cellPixelRectInRegion(
  region: PixelRect,
  grid: SheetSliceGrid,
  col: number,
  row: number,
): PixelRect {
  const cellW = region.sw / grid.cols;
  const cellH = region.sh / grid.rows;
  return {
    sx: Math.round(region.sx + col * cellW),
    sy: Math.round(region.sy + row * cellH),
    sw: Math.max(1, Math.round(cellW)),
    sh: Math.max(1, Math.round(cellH)),
  };
}

export function mergeCellRectInRegion(
  region: PixelRect,
  grid: SheetSliceGrid,
  col0: number,
  row0: number,
  col1: number,
  row1: number,
): PixelRect {
  const minCol = Math.min(col0, col1);
  const maxCol = Math.max(col0, col1);
  const minRow = Math.min(row0, row1);
  const maxRow = Math.max(row0, row1);
  const cellW = region.sw / grid.cols;
  const cellH = region.sh / grid.rows;
  return {
    sx: Math.round(region.sx + minCol * cellW),
    sy: Math.round(region.sy + minRow * cellH),
    sw: Math.max(1, Math.round((maxCol - minCol + 1) * cellW)),
    sh: Math.max(1, Math.round((maxRow - minRow + 1) * cellH)),
  };
}

export function mountHomeSheetGridCrop(
  host: HTMLElement,
  options?: HomeSheetGridCropOptions,
): HomeSheetGridCropHandle {
  let image: HTMLImageElement | null = null;
  let grid: SheetSliceGrid = { cols: 1, rows: 1 };
  let frameRect: PixelRect | null = null;
  let selection: SheetCropSelection | null = null;
  let tool: CropTool = "frame";
  let drag: DragKind | null = null;
  let frameDragOffset = { x: 0, y: 0 };
  let displayScale = 1;
  let resizeCorner: "nw" | "ne" | "sw" | "se" | null = null;
  let resizeAspect = 1;

  host.innerHTML = `
    <div class="home-sheet-grid-crop">
      <div class="home-sheet-crop-toolbar" role="group" aria-label="กริดและโหมดครอป">
        <label class="home-sheet-crop-toolbar-field">
          <span class="home-sheet-crop-toolbar-label">คอล.</span>
          <input type="number" id="sheet-grid-cols" class="text-input home-sheet-grid-num" min="1" max="16" value="1" inputmode="numeric" aria-label="คอลัมน์" />
        </label>
        <label class="home-sheet-crop-toolbar-field">
          <span class="home-sheet-crop-toolbar-label">แถว</span>
          <input type="number" id="sheet-grid-rows" class="text-input home-sheet-grid-num" min="1" max="16" value="1" inputmode="numeric" aria-label="แถว" />
        </label>
        <button type="button" class="home-sheet-crop-tool home-sheet-crop-tool--active" data-crop-tool="frame" aria-pressed="true">
          กรอบ
        </button>
        <button type="button" class="home-sheet-crop-tool" data-crop-tool="grid" aria-pressed="false">
          ช่อง
        </button>
      </div>
      <p id="sheet-grid-selection-hint" class="hint sheet-selection-hint">กรอบทั้งภาพ</p>
      <div class="home-sheet-preview-wrap home-sheet-preview-wrap--crop">
        <canvas class="home-sheet-crop-canvas" aria-label="ครอปภาพและเลือกช่องกริด"></canvas>
      </div>
    </div>
  `;

  const canvas = host.querySelector<HTMLCanvasElement>(".home-sheet-crop-canvas");
  const colsInput = host.querySelector<HTMLInputElement>("#sheet-grid-cols");
  const rowsInput = host.querySelector<HTMLInputElement>("#sheet-grid-rows");
  const selectionHint = host.querySelector<HTMLElement>("#sheet-grid-selection-hint");

  const notify = () => options?.onInteractionChange?.();

  const syncToolTabs = () => {
    host.querySelectorAll<HTMLButtonElement>("[data-crop-tool]").forEach((btn) => {
      const id = btn.dataset.cropTool as CropTool | undefined;
      const on = id === tool;
      btn.classList.toggle("home-sheet-crop-tool--active", on);
      btn.setAttribute("aria-pressed", on ? "true" : "false");
    });
  };

  const syncGridInputs = () => {
    if (colsInput) colsInput.value = String(grid.cols);
    if (rowsInput) rowsInput.value = String(grid.rows);
  };

  const defaultSelection = (): SheetCropSelection => ({
    col: 0,
    row: 0,
    cellsW: 1,
    cellsH: 1,
  });

  const syncSelectionHint = () => {
    if (!selectionHint) return;
    if (!frameRect) {
      selectionHint.textContent = "ยังไม่มีภาพ";
      return;
    }
    const fr = frameRect;
    if (tool === "frame") {
      selectionHint.textContent = `${Math.round(fr.sw)}×${Math.round(fr.sh)} px · ย้ายในกรอบ · มุม=สเกล`;
      return;
    }
    if (!selection) {
      selectionHint.textContent = "ลากเลือกช่องในกรอบ";
      return;
    }
    const { cellsW, cellsH } = selection;
    selectionHint.textContent = `${cellsW}×${cellsH} ช่อง @ ${selection.col + 1},${selection.row + 1}`;
  };

  const computeDisplayScale = (): number => {
    const iw = image?.naturalWidth ?? 0;
    const ih = image?.naturalHeight ?? 0;
    if (!iw || !ih) return 1;
    const wrap = host.querySelector<HTMLElement>(".home-sheet-preview-wrap");
    const pad = 12;
    const wrapW = Math.max(160, (wrap?.clientWidth ?? 280) - pad);
    const wrapH = Math.max(120, (wrap?.clientHeight ?? 240) - pad);
    const fitScale = Math.min(1, wrapW / iw, wrapH / ih);
    const minScale = MIN_CANVAS_LONG_EDGE / Math.max(iw, ih);
    return Math.max(minScale, fitScale);
  };

  const pointerToImage = (clientX: number, clientY: number): { x: number; y: number } | null => {
    if (!canvas || !image?.naturalWidth) return null;
    const rect = canvas.getBoundingClientRect();
    const x = ((clientX - rect.left) / rect.width) * image.naturalWidth;
    const y = ((clientY - rect.top) / rect.height) * image.naturalHeight;
    if (x < 0 || y < 0 || x > image.naturalWidth || y > image.naturalHeight) return null;
    return { x, y };
  };

  const pointerToCellInFrame = (x: number, y: number): { col: number; row: number } | null => {
    if (!frameRect) return null;
    const fr = frameRect;
    if (x < fr.sx || y < fr.sy || x > fr.sx + fr.sw || y > fr.sy + fr.sh) return null;
    const lx = (x - fr.sx) / fr.sw;
    const ly = (y - fr.sy) / fr.sh;
    const col = Math.min(grid.cols - 1, Math.max(0, Math.floor(lx * grid.cols)));
    const row = Math.min(grid.rows - 1, Math.max(0, Math.floor(ly * grid.rows)));
    return { col, row };
  };

  const selectionFromCorners = (
    col0: number,
    row0: number,
    col1: number,
    row1: number,
  ): SheetCropSelection => {
    const minCol = Math.min(col0, col1);
    const maxCol = Math.max(col0, col1);
    const minRow = Math.min(row0, row1);
    const maxRow = Math.max(row0, row1);
    const size = selectionGridSize(minCol, minRow, maxCol, maxRow);
    return { col: minCol, row: minRow, cellsW: size.cellsW, cellsH: size.cellsH };
  };

  const cellInSelection = (col: number, row: number): boolean => {
    if (!selection) return false;
    return (
      col >= selection.col &&
      col < selection.col + selection.cellsW &&
      row >= selection.row &&
      row < selection.row + selection.cellsH
    );
  };

  const frameHandleAt = (clientX: number, clientY: number): "nw" | "ne" | "sw" | "se" | null => {
    if (!canvas || !frameRect || !image?.naturalWidth) return null;
    const rect = canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return null;
    const iw = image.naturalWidth;
    const ih = image.naturalHeight;
    const fr = frameRect;
    const corners = [
      { id: "nw" as const, x: rect.left + (fr.sx / iw) * rect.width, y: rect.top + (fr.sy / ih) * rect.height },
      {
        id: "ne" as const,
        x: rect.left + ((fr.sx + fr.sw) / iw) * rect.width,
        y: rect.top + (fr.sy / ih) * rect.height,
      },
      {
        id: "sw" as const,
        x: rect.left + (fr.sx / iw) * rect.width,
        y: rect.top + ((fr.sy + fr.sh) / ih) * rect.height,
      },
      {
        id: "se" as const,
        x: rect.left + ((fr.sx + fr.sw) / iw) * rect.width,
        y: rect.top + ((fr.sy + fr.sh) / ih) * rect.height,
      },
    ];
    const hitR = HANDLE_R + 2;
    for (const c of corners) {
      const dx = clientX - c.x;
      const dy = clientY - c.y;
      if (dx * dx + dy * dy <= hitR * hitR) return c.id;
    }
    return null;
  };

  const hitFrameInterior = (x: number, y: number): boolean => {
    if (!frameRect) return false;
    const fr = frameRect;
    return x >= fr.sx && x <= fr.sx + fr.sw && y >= fr.sy && y <= fr.sy + fr.sh;
  };

  const paint = () => {
    if (!canvas || !image?.naturalWidth || !frameRect) {
      if (canvas) {
        const ctx = canvas.getContext("2d");
        ctx?.clearRect(0, 0, canvas.width, canvas.height);
      }
      return;
    }

    const iw = image.naturalWidth;
    const ih = image.naturalHeight;
    displayScale = computeDisplayScale();
    const dw = Math.round(iw * displayScale);
    const dh = Math.round(ih * displayScale);
    canvas.width = dw;
    canvas.height = dh;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, dw, dh);
    ctx.drawImage(image, 0, 0, dw, dh);

    const fr = frameRect;
    const fx = fr.sx * displayScale;
    const fy = fr.sy * displayScale;
    const fw = fr.sw * displayScale;
    const fh = fr.sh * displayScale;

    ctx.fillStyle = "rgba(0, 0, 0, 0.55)";
    ctx.fillRect(0, 0, dw, dh);
    ctx.clearRect(fx, fy, fw, fh);
    ctx.drawImage(
      image,
      fr.sx,
      fr.sy,
      fr.sw,
      fr.sh,
      fx,
      fy,
      fw,
      fh,
    );

    const cellDw = fw / grid.cols;
    const cellDh = fh / grid.rows;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
    ctx.lineWidth = 1;
    for (let c = 1; c < grid.cols; c += 1) {
      const x = fx + Math.round(c * cellDw) + 0.5;
      ctx.beginPath();
      ctx.moveTo(x, fy);
      ctx.lineTo(x, fy + fh);
      ctx.stroke();
    }
    for (let r = 1; r < grid.rows; r += 1) {
      const y = fy + Math.round(r * cellDh) + 0.5;
      ctx.beginPath();
      ctx.moveTo(fx, y);
      ctx.lineTo(fx + fw, y);
      ctx.stroke();
    }

    if (tool === "grid" && selection) {
      const { col0, row0, col1, row1 } = selectionToCorners(selection);
      const x = fx + col0 * cellDw;
      const y = fy + row0 * cellDh;
      const w = (col1 - col0 + 1) * cellDw;
      const h = (row1 - row0 + 1) * cellDh;
      ctx.fillStyle = "rgba(79, 210, 255, 0.25)";
      ctx.fillRect(x, y, w, h);
    }

    ctx.strokeStyle = "rgba(79, 210, 255, 0.95)";
    ctx.lineWidth = 2;
    ctx.strokeRect(fx + 0.5, fy + 0.5, fw - 1, fh - 1);

    const drawHandle = (hx: number, hy: number) => {
      ctx.fillStyle = "#4fd2ff";
      ctx.strokeStyle = "#0b1224";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(hx, hy, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    };
    drawHandle(fx, fy);
    drawHandle(fx + fw, fy);
    drawHandle(fx, fy + fh);
    drawHandle(fx + fw, fy + fh);
  };

  const applyGridFromInputs = () => {
    grid = {
      cols: clampSliceCount(Number(colsInput?.value ?? 1)),
      rows: clampSliceCount(Number(rowsInput?.value ?? 1)),
    };
    syncGridInputs();
    selection = defaultSelection();
    syncSelectionHint();
    paint();
    notify();
  };

  colsInput?.addEventListener("change", applyGridFromInputs);
  rowsInput?.addEventListener("change", applyGridFromInputs);

  host.querySelectorAll<HTMLButtonElement>("[data-crop-tool]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const next = btn.dataset.cropTool as CropTool | undefined;
      if (!next || next === tool) return;
      tool = next;
      drag = null;
      if (tool === "grid" && !selection) selection = defaultSelection();
      syncToolTabs();
      syncSelectionHint();
      paint();
      notify();
    });
  });

  const onPointerDown = (event: PointerEvent) => {
    if (!canvas || !image?.naturalWidth || !frameRect) return;
    const pt = pointerToImage(event.clientX, event.clientY);
    if (!pt) return;

    const corner = frameHandleAt(event.clientX, event.clientY);
    if (corner && tool === "frame") {
      resizeCorner = corner;
      resizeAspect = frameRect.sw / Math.max(1, frameRect.sh);
      drag = "frame-resize";
      canvas.setPointerCapture(event.pointerId);
      return;
    }

    if (tool === "frame" && hitFrameInterior(pt.x, pt.y)) {
      drag = "frame-move";
      frameDragOffset = { x: pt.x - frameRect.sx, y: pt.y - frameRect.sy };
      canvas.setPointerCapture(event.pointerId);
      return;
    }

    if (tool === "grid") {
      const cell = pointerToCellInFrame(pt.x, pt.y);
      if (!cell) return;
      if (selection && cellInSelection(cell.col, cell.row)) {
        drag = { type: "selection-move", anchor: cell };
      } else {
        drag = { type: "grid-select", anchor: cell };
        selection = selectionFromCorners(cell.col, cell.row, cell.col, cell.row);
      }
      syncSelectionHint();
      paint();
      notify();
      canvas.setPointerCapture(event.pointerId);
    }
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!drag || !image?.naturalWidth || !frameRect) return;
    const pt = pointerToImage(event.clientX, event.clientY);
    if (!pt) return;
    const iw = image.naturalWidth;
    const ih = image.naturalHeight;

    if (drag === "frame-move") {
      frameRect = clampFrame(
        {
          sx: pt.x - frameDragOffset.x,
          sy: pt.y - frameDragOffset.y,
          sw: frameRect.sw,
          sh: frameRect.sh,
        },
        iw,
        ih,
      );
      syncSelectionHint();
      paint();
      return;
    }

    if (drag === "frame-resize" && resizeCorner) {
      frameRect = resizeFrameUniform(resizeCorner, pt, frameRect, resizeAspect, iw, ih);
      syncSelectionHint();
      paint();
      return;
    }

    if (typeof drag === "object" && drag.type === "grid-select") {
      const cell = pointerToCellInFrame(pt.x, pt.y);
      if (!cell) return;
      selection = selectionFromCorners(
        drag.anchor.col,
        drag.anchor.row,
        cell.col,
        cell.row,
      );
      syncSelectionHint();
      paint();
      notify();
      return;
    }

    if (typeof drag === "object" && drag.type === "selection-move" && selection) {
      const cell = pointerToCellInFrame(pt.x, pt.y);
      if (!cell) return;
      const dCol = cell.col - drag.anchor.col;
      const dRow = cell.row - drag.anchor.row;
      const nextCol = Math.max(0, Math.min(selection.col + dCol, grid.cols - selection.cellsW));
      const nextRow = Math.max(0, Math.min(selection.row + dRow, grid.rows - selection.cellsH));
      selection = { ...selection, col: nextCol, row: nextRow };
      drag = { type: "selection-move", anchor: cell };
      syncSelectionHint();
      paint();
      notify();
    }
  };

  const endDrag = (event: PointerEvent) => {
    if (!drag) return;
    const commitGrid =
      tool === "grid" &&
      selection &&
      typeof drag === "object" &&
      drag.type === "grid-select";
    drag = null;
    resizeCorner = null;
    canvas?.releasePointerCapture(event.pointerId);
    notify();
    if (commitGrid) options?.onGridSelectionCommit?.();
  };

  canvas?.addEventListener("pointerdown", onPointerDown);
  canvas?.addEventListener("pointermove", onPointerMove);
  canvas?.addEventListener("pointerup", endDrag);
  canvas?.addEventListener("pointercancel", endDrag);

  const onResize = () => paint();
  window.addEventListener("resize", onResize);

  syncGridInputs();
  syncToolTabs();
  syncSelectionHint();

  return {
    destroy: () => {
      window.removeEventListener("resize", onResize);
      canvas?.removeEventListener("pointerdown", onPointerDown);
      canvas?.removeEventListener("pointermove", onPointerMove);
      canvas?.removeEventListener("pointerup", endDrag);
      canvas?.removeEventListener("pointercancel", endDrag);
      host.replaceChildren();
    },
    setImage: (next) => {
      if (next === image) {
        paint();
        return;
      }
      image = next;
      if (next?.naturalWidth) {
        frameRect = fullImageFrame(next.naturalWidth, next.naturalHeight);
        selection = defaultSelection();
      } else {
        frameRect = null;
        selection = null;
      }
      drag = null;
      syncSelectionHint();
      paint();
      notify();
    },
    setGrid: (next) => {
      grid = {
        cols: clampSliceCount(next.cols),
        rows: clampSliceCount(next.rows),
      };
      syncGridInputs();
      selection = defaultSelection();
      syncSelectionHint();
      paint();
      notify();
    },
    getGrid: () => ({ ...grid }),
    getFrameRect: () => (frameRect ? { ...frameRect } : null),
    getSelection: () => (selection ? { ...selection } : null),
    clearSelection: () => {
      selection = defaultSelection();
      syncSelectionHint();
      paint();
      notify();
    },
    repaint: paint,
  };
}

export function pixelRectForSheetSelection(
  region: PixelRect,
  grid: SheetSliceGrid,
  sel: SheetCropSelection,
): PixelRect {
  const { col0, row0, col1, row1 } = selectionToCorners(sel);
  return mergeCellRectInRegion(region, grid, col0, row0, col1, row1);
}

export function pixelRectForGridCell(
  region: PixelRect,
  grid: SheetSliceGrid,
  col: number,
  row: number,
): PixelRect {
  return cellPixelRectInRegion(region, grid, col, row);
}
